import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

/**
 * Lange redactionele homepage-tekst. Bewust opgebouwd met H2's en H3's en
 * lopende alinea's, met organische interne links naar de money pages.
 */


export const HOME_FAQ = [
  {
    q: "Welke betaalmethode is het snelst voor een storting?",
    a: "iDEAL, Apple Pay en Google Pay zijn in onze metingen het snelst: het saldo staat er doorgaans binnen tien seconden op.",
  },
  {
    q: "Welke methode betaalt het snelst uit?",
    a: "E-wallets zoals Skrill en Neteller en de bankkoppeling van Trustly laten de kortste doorlooptijden zien. De grootste tijdswinst haal je door je verificatie af te ronden voordat je de eerste opname aanvraagt.",
  },
  {
    q: "Kan ik uitbetalen via iDEAL?",
    a: "Nee. iDEAL is een methode om te betalen, niet om te ontvangen. Je uitbetaling loopt via een gewone bankoverschrijving naar dezelfde rekening, meestal binnen een tot drie werkdagen.",
  },
  {
    q: "Betaal ik kosten over een storting of uitbetaling?",
    a: "Bij de meeste Nederlandse routes niet. Kosten duiken op bij bepaalde wallets, bij creditcardtransacties via je kaartuitgever, bij valutaomrekening en soms bij een tweede opname binnen dezelfde periode.",
  },
  {
    q: "Waarom duurt mijn uitbetaling langer dan bij iemand anders?",
    a: "Meestal door een nog niet afgeronde verificatie, een opnamemethode die niet op jouw naam staat, openstaande bonusvoorwaarden of een aanvraag net na het cut-off-moment of vlak voor het weekend.",
  },
  {
    q: "Kan ik met een klein bedrag beginnen?",
    a: "Ja. Bij veel aanbieders ligt het minimum via iDEAL laag, en met een prepaid tegoed bepaal je het bedrag zelf. In de casinozoeker kun je filteren op minimale storting.",
  },
  {
    q: "Hoe actueel zijn deze gegevens?",
    a: "Elke pagina toont de datum van de laatste controle en de naam van de redacteur en de factchecker. Wijzigingen leggen we vast in het overzicht van laatste controles.",
  },
];

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="max-w-3xl">
        <h2 id={id} className="text-2xl font-bold tracking-tight text-foreground">
          {title}
        </h2>
        <div className="mt-4 space-y-4 text-[15px] leading-7 text-muted-foreground [&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:decoration-primary/40 [&_a]:underline-offset-4 [&_h3]:mt-8 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground [&_li]:leading-7 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5 hover:[&_a]:decoration-primary">
          {children}
        </div>
      </div>
    </section>
  );
}

function M({ slug, children }: { slug: string; children: ReactNode }) {
  return (
    <Link to="/betaalmethoden/$slug/" params={{ slug }}>
      {children}
    </Link>
  );
}

export function HomeEditorial() {
  return (
    <div className="border-t border-border bg-secondary/30">
      <Section id="betaalmethode-kiezen" title="Zo kies je de juiste betaalmethode voor een online casino">
        <p>
          De betaalmethode is het onderdeel van een casinoaccount waar spelers het vaakst tegenaan
          lopen en er tegelijk het minst over lezen. Bonussen en spelaanbod verschillen minder van
          elkaar dan de kassa: de ene aanbieder schrijft een storting binnen enkele seconden bij en
          betaalt binnen een dag terug, de andere doet daar dagen over, rekent kosten door of
          accepteert een methode alleen voor stortingen. Wij meten die verschillen zelf, met echte
          transacties en eigen geld, en leggen elke meting per aanbieder en per methode vast. Op deze
          pagina vind je de samenvatting; per methode staat het volledige testrapport op de losse
          pagina.
        </p>
        <p>
          De keuze begint bij één vraag: wil je vooral snel storten, of wil je vooral snel je winst
          terugzien? Dat lijkt hetzelfde, maar het is het niet. In Nederland is{" "}
          <M slug="ideal">iDEAL</M> veruit de snelste en meest vertrouwde manier om geld op je
          spelersrekening te zetten, maar het is geen retourmethode: je uitbetaling loopt dan alsnog
          via een gewone bankoverschrijving. Wie het hele traject via dezelfde route wil laten lopen,
          komt eerder uit bij <M slug="trustly">Trustly</M> of bij een e-wallet zoals{" "}
          <M slug="skrill">Skrill</M> of <M slug="neteller">Neteller</M>, waar het bedrag terugkomt
          op hetzelfde saldo waarmee je hebt gestort.
        </p>
        <p>
          Naast de kassa telt ook wat een aanbieder rekenkundig teruggeeft. Het gemiddelde
          uitbetalingspercentage van de lobby bepaalt hoeveel van je inzet over veel spelrondes
          terugkomt, en dat verschil weegt op de lange termijn zwaarder dan welke bonus dan ook.
          Welke aanbieders daarop het gunstigst scoren, staat in onze analyse van de{" "}
          <Link to="/best-uitbetalende-online-casinos/">best uitbetalende online casino&apos;s</Link>.
        </p>
        <h3>Vier vragen die je keuze bepalen</h3>
        <p>
          Stel jezelf voordat je stort vier vragen. Hoeveel wil je per keer storten, en past dat
          binnen de minimale en maximale limieten van de methode? Wil je met een vast budget spelen
          dat niet aan je bankrekening vastzit? Hoe snel wil je bij een uitbetaling je geld terug?
          En hoeveel identificatie ben je bereid te doorlopen voordat een uitbetaling wordt
          vrijgegeven? Die vier antwoorden sturen je bijna altijd naar dezelfde twee of drie
          methoden, en dan is de rest een kwestie van de juiste aanbieder kiezen.
        </p>
        <p>
          Wie een klein bedrag wil inleggen zonder de bankrekening te belasten, komt uit bij{" "}
          <M slug="paysafecard">Paysafecard</M>: je koopt een tegoedcode in de winkel of online en
          stort daarmee. Wie op de bank betaalt en snelheid belangrijk vindt, kiest{" "}
          <M slug="apple-pay">Apple Pay</M> of <M slug="google-pay">Google Pay</M>, waarbij je de
          betaling met gezichtsherkenning of vingerafdruk bevestigt en dus geen kaartgegevens meer
          hoeft in te typen. En wie internationaal speelt of gewend is aan kaartbetalingen, gebruikt
          een <M slug="creditcard">creditcard</M> van <M slug="visa">Visa</M> of{" "}
          <M slug="mastercard">Mastercard</M>.
        </p>
        <h3>Snelheid is niet hetzelfde als beschikbaarheid</h3>
        <p>
          Een methode die theoretisch binnen seconden werkt, is nutteloos als jouw aanbieder haar
          niet ondersteunt. Daarom staat naast elke methode welke aanbieders haar in de praktijk
          accepteren en of dat ook voor uitbetalingen geldt. Dat laatste is de meest gemaakte
          denkfout: veel spelers gaan ervan uit dat geld terugkomt via de route waarmee ze stortten,
          en ontdekken pas bij de eerste opname dat ze een bankrekening moeten opgeven en aanvullende
          documenten moeten aanleveren. Wil je die verrassing voorkomen, gebruik dan de{" "}
          <Link to="/casinozoeker/">casinozoeker</Link> en filter direct op opnamemogelijkheid.
        </p>
      </Section>

      <Section id="storten-en-uitbetalen" title="Storten en uitbetalen: wat er achter de schermen gebeurt">
        <p>
          Een storting en een uitbetaling zijn technisch gezien twee volledig verschillende
          processen, en dat verklaart waarom de doorlooptijden zo ver uit elkaar liggen. Bij een
          storting duwt jouw bank of wallet geld richting de aanbieder. Die transactie is
          onomkeerbaar bevestigd op het moment dat je bank akkoord geeft, dus het casino kan het
          saldo meteen vrijgeven zonder risico. Daarom zie je in onze metingen dat de meeste
          stortingen binnen vijf tot vijftien seconden zichtbaar zijn, ongeacht welke aanbieder je
          gebruikt.
        </p>
        <p>
          Een uitbetaling loopt de andere kant op en raakt daarmee aan verplichtingen die de
          aanbieder zelf moet nakomen. Voordat er geld je kant op gaat, controleert de
          betalingsafdeling of het account geverifieerd is, of de opnamemethode op jouw naam staat,
          of er geen openstaande bonusvoorwaarden zijn en of het speelpatroon geen signalen bevat die
          nader onderzoek vragen. Pas daarna gaat de opdracht de deur uit. De doorlooptijd die wij
          meten bestaat dus uit twee delen: de interne verwerkingstijd van de aanbieder en de
          transporttijd van de betaalmethode zelf.
        </p>
        <h3>Waarom de interne verwerkingstijd het meeste verschil maakt</h3>
        <p>
          In onze meetreeks zien we dat het transport zelden het probleem is. Een SEPA-overboeking
          binnen Nederland is doorgaans binnen enkele uren of de volgende werkdag bijgeschreven, en
          een e-wallet ontvangt het bedrag vrijwel direct zodra de opdracht is verstuurd. Het verschil
          tussen een aanbieder die binnen vier uur uitbetaalt en een die er drie dagen over doet, zit
          bijna volledig in de eigen goedkeuringsketen. Aanbieders die opnames automatisch
          goedkeuren zodra het account geverifieerd is, scoren structureel beter dan aanbieders die
          elke opname handmatig laten beoordelen.
        </p>
        <p>
          Daarom kijken we bij een aanbieder niet alleen naar de gemiddelde uitbetalingstijd, maar
          ook naar de spreiding. Een aanbieder die de ene keer in twee uur en de andere keer in vier
          dagen uitbetaalt, is voor een speler onvoorspelbaarder dan een aanbieder die altijd rond de
          vierentwintig uur zit. Voorspelbaarheid weegt in onze rangschikking net zo zwaar als de
          snelste meting, omdat je in de praktijk plant op wat je meestal kunt verwachten.
        </p>
        <h3>Weekenden, feestdagen en het cut-off-moment</h3>
        <p>
          Betaalverkeer volgt bankdagen. Een uitbetaling die op vrijdagavond wordt goedgekeurd, kan
          bij een bankoverschrijving pas maandag vertrekken; via een e-wallet komt hij vaak nog
          dezelfde avond aan. Dat is een van de belangrijkste redenen om een wallet te gebruiken als
          je in het weekend speelt. Aanbieders hanteren daarnaast een cut-off-tijd: opdrachten na een
          bepaald tijdstip schuiven door naar de volgende batch. Wij noteren onze aanvraagtijd bij
          elke test, zodat de gemeten doorlooptijden onderling vergelijkbaar blijven.
        </p>
      </Section>

      <Section id="kosten" title="Kosten, limieten en wisselkoersen bij casinobetalingen">
        <p>
          De meeste Nederlandse spelers betalen niets voor een storting. Toch zijn er drie plekken
          waar geld weglekt, en die zijn niet altijd zichtbaar op het moment dat je betaalt. De
          eerste is een expliciete transactiekosten-post bij bepaalde e-wallets of kaartbetalingen.
          De tweede is een valutakoers: speel je in euro's maar loopt je wallet of kaart in een
          andere valuta, dan betaal je een opslag op de wisselkoers die vaak groter is dan de
          zichtbare kosten. De derde is een inactiviteitskosten-regeling of een kosten-post bij een
          tweede opname binnen dezelfde maand.
        </p>
        <p>
          Bij <M slug="paypal">PayPal</M> en bij e-wallets kom je die opslag het snelst tegen, en bij
          een <M slug="creditcard">creditcard</M> kan je eigen kaartuitgever bovendien een aparte
          behandeling toepassen op transacties richting kansspelaanbieders. Wij noteren per methode
          welke kostenposten wij tijdens onze eigen tests daadwerkelijk hebben zien afgaan, en welke
          alleen in de voorwaarden staan zonder dat wij ze in de praktijk hebben gemeten. Dat
          onderscheid staat er expliciet bij, want een kostenpost in de kleine lettertjes is iets
          anders dan een kostenpost op je afschrift.
        </p>
        <h3>Minimale storting en waarom die per methode verschilt</h3>
        <p>
          De minimale storting is vaker een eigenschap van de betaalmethode dan van de aanbieder. Bij{" "}
          <M slug="ideal">iDEAL</M> ligt het minimum meestal laag, omdat de kosten per transactie
          voor de aanbieder beperkt zijn. Bij kaartbetalingen en sommige wallets ligt het minimum
          hoger, omdat er per transactie een vast bedrag aan verwerkingskosten tegenover staat. Wie
          bewust met kleine bedragen wil spelen, kan in de{" "}
          <Link to="/casinozoeker/">casinozoeker</Link> op stortlimiet filteren en zo direct de
          combinaties zien die dat toestaan.
        </p>
        <p>
          Maximale limieten spelen vooral bij uitbetalingen. Veel aanbieders hanteren een maximum per
          dag, week of maand, en een grote winst wordt dan in termijnen uitbetaald. Dat is geen
          onwil, maar staand beleid dat samenhangt met risicobeheer en met de controle op grote
          bedragen. Voor de meeste spelers is het irrelevant; voor wie een grote winst pakt, is het
          precies het detail dat het verschil maakt tussen twee weken wachten en één avond.
        </p>
        <h3>Bonusvoorwaarden en uitgesloten betaalmethoden</h3>
        <p>
          Een deel van de aanbieders sluit bepaalde betaalmethoden uit van bonusdeelname. Dat komt
          het vaakst voor bij e-wallets, omdat die historisch gevoeliger zijn voor misbruik van
          welkomstaanbiedingen. Stort je met een uitgesloten methode, dan krijg je de bonus niet,
          ook niet met terugwerkende kracht. Controleer dit vóór je stort en niet erna; wij vermelden
          het per methode wanneer wij het tijdens onze tests zijn tegengekomen.
        </p>
      </Section>

      <Section id="veiligheid" title="Veiligheid, verificatie en je eigen naam op de rekening">
        <p>
          Elke legale aanbieder controleert je identiteit voordat er geld naar buiten gaat. Dat heet
          verificatie, en het gebeurt op basis van wettelijke verplichtingen rond het voorkomen van
          witwassen en het beschermen van spelers. In de praktijk betekent het dat je een
          identiteitsbewijs uploadt, soms een adresbewijs, en dat de betaalmethode waarmee je opneemt
          op jouw eigen naam moet staan. Een rekening van een partner, ouder of vriend wordt
          geweigerd, en dat is geen fout van de aanbieder maar de regel.
        </p>
        <p>
          De slimste zet die je als speler kunt doen, is verificatie afronden vóór je eerste
          uitbetaling aanvraagt in plaats van erna. Bij aanbieders die documenten pas bij de eerste
          opname beoordelen, verdwijnt de helft van de doorlooptijd in dat proces. Ronde je het van
          tevoren af, dan meten wij bij dezelfde aanbieder vaak een uitbetaling die uren in plaats van
          dagen duurt. Dit is de enige variabele in het hele traject waar je als speler zelf volledige
          controle over hebt.
        </p>
        <p>
          De snelste route daarvoor is verifiëren via je eigen bank. Bij{" "}
          <Link to="/idin-casinos/">iDIN casino&apos;s</Link> deel je met één bevestiging in je
          bankapp je geverifieerde naam, adres en geboortedatum, zonder paspoortfoto. In onze
          testronde stond een account zo mediaan in minder dan twee minuten volledig klaar.
        </p>
        <h3>Naamcontrole en de retourroute</h3>
        <p>
          Bij <M slug="ideal">iDEAL</M> en bij bankoverschrijvingen wordt de tenaamstelling
          automatisch meegestuurd, waardoor de controle vrijwel geruisloos verloopt. Bij prepaid
          methoden zoals <M slug="paysafecard">Paysafecard</M> ontbreekt die koppeling: de code zelf
          draagt geen naam, dus de aanbieder vraagt bij een uitbetaling alsnog om een bankrekening op
          jouw naam. Dat verklaart waarom prepaid uitstekend werkt om je inleg te beheersen, maar
          nooit de snelste uitbetalingsroute is.
        </p>
        <p>
          Bij wallets als <M slug="skrill">Skrill</M>, <M slug="neteller">Neteller</M> en{" "}
          <M slug="revolut">Revolut</M> ligt het anders: het account is al op naam geverifieerd, dus
          de aanbieder kan het bedrag zonder extra tussenstap terugsturen. Dat is de belangrijkste
          reden dat wallets in onze metingen de kortste uitbetalingstijden laten zien, ook bij
          aanbieders die verder niet uitblinken in snelheid.
        </p>
        <h3>Waar je op moet letten bij de betaalpagina zelf</h3>
        <ul>
          <li>
            Controleer dat de betaalpagina op het domein van je eigen bank of de betaaldienst staat en
            niet op een tussendomein dat er slechts op lijkt.
          </li>
          <li>
            Bevestig een betaling nooit op verzoek van iemand die je telefonisch of via chat
            benadert; een aanbieder vraagt nooit om een betaalbevestiging buiten de kassa om.
          </li>
          <li>
            Gebruik voor je spelersaccount een uniek wachtwoord en zet tweestapsverificatie aan als
            de aanbieder dat aanbiedt.
          </li>
          <li>
            Bewaar de bevestigingsmails van je stortingen en opnames; bij een geschil is dat je
            eenvoudigste bewijs.
          </li>
        </ul>
      </Section>

      <Section id="methodes-vergeleken" title="De belangrijkste betaalmethoden naast elkaar">
        <p>
          Hieronder staat per categorie wat de methode goed doet, waar ze tekortschiet en voor welke
          speler ze logisch is. Elke methode heeft een eigen pagina met het volledige testrapport,
          de feitentabel en de aanbieders die haar ondersteunen.
        </p>
        <h3>Bankmethoden: iDEAL, Trustly en Sofort</h3>
        <p>
          <M slug="ideal">iDEAL</M> is in Nederland de standaard. Je kiest je bank, bevestigt in de
          bankapp en het bedrag staat er vrijwel direct op. In onze meetreeks lag de mediaan rond de
          zeven seconden, en dat verschilde nauwelijks tussen aanbieders. De beperking is bekend:
          iDEAL is een pushmethode en werkt niet als retourroute, dus je uitbetaling loopt via een
          gewone overschrijving naar dezelfde rekening.
        </p>
        <p>
          <M slug="trustly">Trustly</M> lijkt op iDEAL, maar werkt wél twee kanten op. Je koppelt je
          bankrekening en de aanbieder kan het bedrag via dezelfde verbinding terugsturen, waardoor je
          geen aparte opnameroute hoeft op te geven. Voor spelers die vooral op de bank willen blijven
          en toch snel willen worden uitbetaald, is dit meestal de sterkste combinatie.{" "}
          <M slug="sofort">Sofort</M> vervult een vergelijkbare rol maar is in Nederland minder breed
          uitgerold en wordt vaker als aanvullende optie aangeboden.
        </p>
        <h3>Kaarten: Visa, Mastercard en creditcard in het algemeen</h3>
        <p>
          Een <M slug="creditcard">creditcard</M> is de meest universele optie: bijna elke aanbieder
          accepteert hem en je hoeft geen extra account aan te maken. <M slug="visa">Visa</M> en{" "}
          <M slug="mastercard">Mastercard</M> zijn daarbij de twee netwerken die je in de praktijk
          tegenkomt, en de verschillen tussen beide zijn voor een speler klein. De storting is snel;
          de uitbetaling loopt als terugboeking naar dezelfde kaart en duurt in onze metingen
          doorgaans één tot drie werkdagen, afhankelijk van je kaartuitgever.
        </p>
        <p>
          Let bij kaarten op twee dingen. Sommige uitgevers behandelen transacties richting
          kansspelaanbieders als een contantopname met bijbehorende kosten en rente vanaf dag één. En
          een debitcard werkt niet altijd waar een creditcard dat wel doet, ook al staat hetzelfde
          logo op het pasje. Controleer dat in de kassa voordat je een storting probeert.
        </p>
        <h3>Wallets: PayPal, Skrill, Neteller, Revolut en HiPay</h3>
        <p>
          <M slug="paypal">PayPal</M> is de bekendste wallet en werkt bij de aanbieders die haar
          ondersteunen prettig en snel, in beide richtingen. Niet elke aanbieder biedt haar aan, dus
          controleer dat vooraf. <M slug="skrill">Skrill</M> en <M slug="neteller">Neteller</M> zijn
          specifiek in deze sector breed geaccepteerd en behoren in onze metingen tot de snelste
          uitbetalingsroutes, met de kanttekening dat ze vaker van bonusdeelname worden uitgesloten.
        </p>
        <p>
          <M slug="revolut">Revolut</M> zit tussen bank en wallet in: je hebt een volwaardige rekening
          met IBAN, maar de app-gedreven werking en de directe meldingen maken het gebruik meer op een
          wallet lijken. <M slug="hipay">HiPay</M> is een verwerker die je bij een deel van de
          aanbieders in de kassa tegenkomt en die meerdere onderliggende methoden bundelt.
        </p>
        <h3>Mobiel en prepaid: Apple Pay, Google Pay en Paysafecard</h3>
        <p>
          <M slug="apple-pay">Apple Pay</M> en <M slug="google-pay">Google Pay</M> zijn de snelste
          opties in de kassa, simpelweg omdat je niets hoeft in te typen: je bevestigt met je gezicht
          of vingerafdruk en de betaling is klaar. Onderliggend gebruiken ze je kaart of bankrekening,
          dus de uitbetalingsroute is dezelfde als bij die onderliggende methode.
        </p>
        <p>
          <M slug="paysafecard">Paysafecard</M> is de enige echte prepaid optie in dit rijtje. Je
          koopt een tegoed en dat tegoed is je plafond; er is geen verbinding met je bankrekening. Dat
          maakt het de sterkste keuze voor iedereen die vooraf een grens wil trekken. De uitbetaling
          loopt altijd via een andere route, dus reken erop dat je daarvoor alsnog een geverifieerde
          bankrekening opgeeft.
        </p>
      </Section>

      <Section id="testmethode" title="Hoe wij testen en waarom onze cijfers verschillen van marketingclaims">
        <p>
          Alle cijfers op deze site komen uit eigen transacties. Wij openen een account als gewone
          speler, ronden de verificatie af, storten met eigen geld en vragen daarna een uitbetaling
          aan. Van elke stap noteren wij het tijdstip: het moment van bevestiging in de bankapp of
          wallet, het moment waarop het saldo zichtbaar werd, het moment waarop wij de opname
          aanvroegen, het moment van goedkeuring en het moment van bijschrijving. Die vijf tijdstempels
          vormen de basis van elke doorlooptijd die je bij ons leest.
        </p>
        <p>
          Wij gebruiken geen tijden die een aanbieder zelf communiceert. "Binnen 24 uur" is een belofte,
          geen meting, en in onze reeks wijken de werkelijke tijden daar regelmatig van af — beide
          kanten op. Waar een aanbieder sneller is dan hij belooft, schrijven wij dat net zo hard op als
          wanneer hij trager is. Alle metingen zijn terug te lezen op de pagina van de betreffende
          methode en in het testblok op deze pagina.
        </p>
        <h3>Wat we bewust niet doen</h3>
        <p>
          Wij plaatsen geen aanbieder hoger omdat daar een commerciële afspraak achter zit. Onze
          volgorde volgt vaste criteria: gemeten uitbetalingssnelheid, spreiding in die snelheid,
          kosten, minimale storting, breedte van het methodenaanbod en het gemak van het
          verificatietraject. Wij verzinnen geen cijfers wanneer een test niet is uitgevoerd; dan staat
          er dat de meting ontbreekt. En wij publiceren geen onvergunde aanbieders, ook niet als daar
          vraag naar zou zijn.
        </p>
        <p>
          Elke pagina heeft een redacteur die haar schrijft en een tweede persoon die de feiten
          controleert. Beide namen staan bij het artikel, met de datum van de laatste controle. Wie de
          mensen achter deze metingen zijn en welke achtergrond zij hebben, lees je bij{" "}
          <Link to="/auteurs/">onze redactie</Link>. Vragen of correcties kun je via{" "}
          <Link to="/contact/">de contactpagina</Link> doorgeven; feitelijke fouten corrigeren wij en
          vermelden wij bij de update.
        </p>
        <h3>Hoe vaak we hertesten</h3>
        <p>
          Betaalgegevens verouderen snel. Aanbieders wisselen van verwerker, voegen methoden toe of
          halen ze weg, en verwerkingstijden schuiven mee met beleidswijzigingen. Wij hertesten daarom
          periodiek en leggen elke wijziging vast in het overzicht van laatste controles op deze
          pagina. Zie je een verschil tussen wat je zelf ervaart en wat hier staat, laat het weten —
          dat is voor ons vaak het eerste signaal dat een aanbieder iets heeft veranderd.
        </p>
      </Section>

      <Section id="verantwoord" title="Verantwoord spelen begint bij je betaalgedrag">
        <p>
          Deze site is uitsluitend bedoeld voor volwassenen van 18 jaar en ouder. Spelen kost geld en
          hoort een uitgave te zijn waar je plezier aan beleeft, niet een manier om geld te verdienen
          of verliezen terug te halen. De betaalmethode die je kiest heeft daar meer invloed op dan de
          meeste mensen denken: hoe makkelijker het is om nog een storting te doen, hoe minder je merkt
          hoeveel er in totaal is gegaan.
        </p>
        <p>
          Wie grip wil houden, doet er verstandig aan om vooraf een bedrag te bepalen en een methode te
          kiezen die dat bedrag afdwingt. Een prepaid tegoed via{" "}
          <M slug="paysafecard">Paysafecard</M> is daar het duidelijkste voorbeeld van: op is op. Ook
          de stortlimieten die je bij de aanbieder zelf kunt instellen zijn effectief, juist omdat ze
          werken op het moment dat je er niet meer rationeel over nadenkt.
        </p>
        <h3>Signalen dat het te ver gaat</h3>
        <ul>
          <li>Je stort vaker of meer dan je van tevoren had bepaald.</li>
          <li>Je gebruikt geld dat voor vaste lasten bedoeld was.</li>
          <li>Je speelt om verlies terug te winnen in plaats van voor het spel zelf.</li>
          <li>Je houdt verborgen voor je omgeving hoeveel je speelt of stort.</li>
        </ul>
        <p>
          Herken je jezelf hierin, stel dan een limiet in, neem een pauze via de mogelijkheden die
          aanbieders bieden, of zoek hulp. Praten met iemand helpt eerder dan wachten tot het vanzelf
          overgaat. Wij promoveren nooit aanbieders of routes die deze bescherming omzeilen, en je
          vindt op deze site dan ook geen enkele verwijzing naar het spelen buiten het Nederlandse
          stelsel om.
        </p>
      </Section>

      <Section id="veelgestelde-vragen" title="Veelgestelde vragen over casinobetalingen">
        <h3>Welke betaalmethode is het snelst voor een storting?</h3>
        <p>
          In onze metingen zijn <M slug="ideal">iDEAL</M>, <M slug="apple-pay">Apple Pay</M> en{" "}
          <M slug="google-pay">Google Pay</M> het snelst in de kassa: het saldo staat er doorgaans
          binnen tien seconden op. Het verschil tussen deze drie is voor de speler in de praktijk
          verwaarloosbaar; de keuze hangt vooral af van welk apparaat je gebruikt.
        </p>
        <h3>Welke methode betaalt het snelst uit?</h3>
        <p>
          E-wallets zoals <M slug="skrill">Skrill</M> en <M slug="neteller">Neteller</M> en de
          bankkoppeling van <M slug="trustly">Trustly</M> laten de kortste doorlooptijden zien, omdat
          het bedrag zonder tussenstap terugkomt op een al geverifieerd account. De grootste
          tijdswinst haal je echter door je verificatie af te ronden vóórdat je de eerste opname
          aanvraagt.
        </p>
        <h3>Kan ik uitbetalen via iDEAL?</h3>
        <p>
          Nee. iDEAL is een methode om te betalen, niet om te ontvangen. Aanbieders sturen je
          uitbetaling via een gewone bankoverschrijving naar dezelfde rekening waarmee je stortte, wat
          in de praktijk meestal binnen één tot drie werkdagen staat bijgeschreven.
        </p>
        <h3>Betaal ik kosten over een storting of uitbetaling?</h3>
        <p>
          Bij de meeste Nederlandse routes niet. Kosten duiken op bij bepaalde wallets, bij
          creditcardtransacties via je eigen kaartuitgever, bij valutaomrekening en soms bij een
          tweede opname binnen dezelfde periode. Per methode staat vermeld welke kosten wij zelf op
          ons afschrift hebben zien terugkomen.
        </p>
        <h3>Waarom duurt mijn uitbetaling langer dan bij iemand anders?</h3>
        <p>
          De meest voorkomende oorzaken zijn een nog niet afgeronde verificatie, een opnamemethode die
          niet op jouw naam staat, openstaande bonusvoorwaarden of een aanvraag die net na het
          cut-off-moment of vlak voor een weekend is ingediend. Alle vier zijn ze zichtbaar in je
          accountoverzicht voordat je de opname aanvraagt.
        </p>
        <h3>Kan ik met een klein bedrag beginnen?</h3>
        <p>
          Ja. Bij veel aanbieders ligt het minimum via <M slug="ideal">iDEAL</M> laag, en met een
          prepaid tegoed bepaal je het bedrag zelf. In de{" "}
          <Link to="/casinozoeker/">casinozoeker</Link> kun je direct filteren op minimale storting,
          zodat je alleen combinaties overhoudt die bij jouw budget passen.
        </p>
        <h3>Hoe actueel zijn deze gegevens?</h3>
        <p>
          Elke pagina toont de datum van de laatste controle en de naam van de redacteur en de
          factchecker. Wijzigingen leggen we vast in het overzicht van laatste controles, en het
          volledige overzicht van methoden vind je op de pagina met{" "}
          <Link to="/betaalmethoden/">alle betaalmethoden</Link>. Recente ontwikkelingen in het
          betaallandschap volgen we in <Link to="/nieuws/">ons nieuws</Link>.
        </p>
      </Section>
    </div>
  );
}
